'use client';

import { useMemo, useState, useSyncExternalStore } from 'react';
import dynamic from 'next/dynamic';
import CirclePlusIcon from '../Icons/CirclePlusIcon';
import LocationIcon from '../Icons/LocationIcon';
import OfferDetails from '../OfferCard/OfferDetails';
import { HeaderEyebrowProps } from './types';

const HeaderLocationDrawer = dynamic(() => import('./HeaderLocationDrawer'), { ssr: false });
import {
  getHeaderLocationServerSnapshot,
  getHeaderLocationSnapshot,
  subscribeHeaderLocation,
  writeHeaderLocation,
  getHeaderLocationLoading,
} from '@/lib/utils/headerLocationStorage';

export function HeaderEyebrow({
  couponOffer,
  brandCode,
  locationLabel = 'Enter Zip Code',
  showLocationOnMobile = false,
}: HeaderEyebrowProps) {
  const [isOfferOpen, setIsOfferOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  // Defer loading the drawer's JS chunk until the user first opens it.
  const [hasOpenedLocation, setHasOpenedLocation] = useState(false);

  const selectedLocation = useSyncExternalStore(
    (callback) => subscribeHeaderLocation(brandCode, callback),
    () => getHeaderLocationSnapshot(brandCode),
    getHeaderLocationServerSnapshot,
  );

  const isLocationLoading = useSyncExternalStore(
    (callback) => subscribeHeaderLocation(brandCode, callback),
    getHeaderLocationLoading,
    () => true,
  );

  const resolvedLocationLabel = useMemo(() => {
    if (!selectedLocation) return locationLabel;
    return `${selectedLocation.locality}, ${selectedLocation.stateShort}`;
  }, [selectedLocation, locationLabel]);

  return (
    <>
      <div className='xl:px-12 px-3 h-10 bg-global-brand-primary flex items-center xl:justify-between justify-center'>
        <div className='flex items-center xl:gap-2 gap-3 min-w-0'>
          {couponOffer ? (
            <button
              type='button'
              onClick={() => setIsOfferOpen(true)}
              className='flex items-center xl:gap-2 gap-3 min-w-0 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-primary-500 rounded-sm'
            >
              <CirclePlusIcon className='text-brand-icon w-4 h-4 shrink-0' />
              <span className='text-sm text-white font-semibold tracking-[-0.14px] truncate'>
                {couponOffer.title}
              </span>
            </button>
          ) : null}
        </div>

        <div className={`${showLocationOnMobile ? 'flex' : 'hidden xl:flex'} items-center gap-2`}>
          <button
            type='button'
            onClick={() => {
              setHasOpenedLocation(true);
              setIsLocationOpen(true);
            }}
            className='flex items-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-primary-500 rounded-sm'
          >
            <LocationIcon className='text-brand-icon size-9' />
            <span
              className={`text-sm text-white font-semibold tracking-[-0.14px] truncate transition-opacity duration-50 ${
                isLocationLoading ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {resolvedLocationLabel}
            </span>
          </button>
        </div>
      </div>

      <OfferDetails
        data={couponOffer ?? null}
        isOpen={isOfferOpen}
        onClose={() => setIsOfferOpen(false)}
      />

      {hasOpenedLocation && (
        <HeaderLocationDrawer
          isOpen={isLocationOpen}
          onClose={() => setIsLocationOpen(false)}
          selectedLocation={selectedLocation}
          onSave={(location) => {
            writeHeaderLocation(brandCode, location);
            setIsLocationOpen(false);
          }}
        />
      )}
    </>
  );
}
